import Chart from 'chart.js';
import { resolveCssVars, ThemeMode, tokensByMode } from './tokens';

type Resolved = { original: string; resolved: string };

// For each object we rewrote: key -> the `var(--…)` string it came from.
// Lets a later update (e.g. after a theme switch) resolve from the original
// again, while still noticing when React has handed the chart a new value.
const sources = new WeakMap<object, Record<string, Resolved>>();

const MAX_DEPTH = 6;

// Chart.js builds `options`, `scales` and `ticks` with prototype chains
// rather than object literals, so an exact `Object.prototype` check would
// skip them. Anything whose chain holds a class (DOM nodes, Date, canvas
// contexts) is still left alone.
const isPlainContainer = (value: unknown): value is object => {
  if (Array.isArray(value)) return true;
  if (!value || typeof value !== 'object') return false;
  let proto = Object.getPrototypeOf(value);
  while (proto && proto !== Object.prototype) {
    if (typeof proto.constructor === 'function' && proto.constructor !== Object)
      return false;
    proto = Object.getPrototypeOf(proto);
  }
  return true;
};

const resolveNode = (node: any, depth = 0) => {
  if (!node || typeof node !== 'object' || depth > MAX_DEPTH) return;
  const remembered = sources.get(node) || {};
  Object.keys(node).forEach((key) => {
    if (key.charAt(0) === '_') return;
    const value = node[key];
    if (typeof value === 'string') {
      const seen = remembered[key];
      const original = seen && seen.resolved === value ? seen.original : value;
      if (original.indexOf('var(') === -1) return;
      const resolved = resolveCssVars(original);
      node[key] = resolved;
      remembered[key] = { original, resolved };
    } else if (isPlainContainer(value)) {
      resolveNode(value, depth + 1);
    }
  });
  sources.set(node, remembered);
};

// Dataset and option colors are written with tokens (`colors.brand`) like
// every other style; this turns them into concrete colors before drawing.
Chart.plugins.register({
  beforeUpdate(chart: any) {
    resolveNode(chart.config.data);
    resolveNode(chart.options);
  },
});

/**
 * Chart.js draws to <canvas>, which can't read CSS variables, so chart colors
 * are applied as concrete values from the active token set instead. Called by
 * ThemeModeProvider whenever the mode changes.
 */
export const applyChartTheme = (mode: ThemeMode) => {
  const t = tokensByMode[mode];
  const defaults: any = Chart.defaults;
  defaults.global.defaultFontColor = t.textSecondary;
  defaults.scale.gridLines.color = t.border;
  defaults.scale.gridLines.zeroLineColor = t.borderStrong;

  // Charts already on screen were built with the previous colors.
  const instances: any = (Chart as any).instances || {};
  Object.keys(instances).forEach((id) => {
    const chart = instances[id];
    const scales = (chart.options && chart.options.scales) || {};
    [...(scales.xAxes || []), ...(scales.yAxes || [])].forEach((axis: any) => {
      axis.gridLines = {
        ...axis.gridLines,
        ...(axis.gridLines && axis.gridLines.display === false
          ? {}
          : { color: t.border, zeroLineColor: t.borderStrong }),
      };
    });
    if (chart.options && chart.options.legend && chart.options.legend.labels) {
      chart.options.legend.labels.fontColor = t.textSecondary;
    }
    chart.update();
  });
};
